import { ResponsiveBarCanvas } from '@nivo/bar';
import _ from 'lodash';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilValue } from 'recoil';
import { TwTheme } from '../../../Tw';
import { getBestContrastColor } from '../../common/color.helper';
import { formatInteger } from '../../common/formatHelper';
import useGetNamedColor from '../../common/useGetNamedColor';
import { commonMarginConfig } from '../../common/utils/chartConfig';
import ContainerChart from '../../simulation/panels/ContainerChart';
import useActualityAggregationFieldDescriptors from '../hooks/useActualityAggregationFieldDescriptors';
import {
  actualityAggregatedStatsGroupBy,
  actualityAggregatedStatsSelectedAggregatedMetric,
} from '../store/actuality.kpi.state';
import ActualityAggregatedStatsBarChartTooltip from './ActualityAggregatedStatsBarChartTooltip';
import { ActualityAggregatedStatsTimelineHeatmapItem } from './ActualityAggregatedStatsHeatmapChart';
import { formatChartAxisLabel } from './chartUtils';

const colors = TwTheme.extend.colors;
const theme = TwTheme.extend.colors.chart;

export type ActualityAggregatedStatsBarChartProps = {
  data: ActualityAggregatedStatsTimelineHeatmapItem[];
  fullHeight: boolean;
  compareMode?: boolean;
  xAxisField?: string; // The field being displayed on x-axis for proper formatting
  showAxisX?: boolean;
  showAxisY?: boolean;
};

const ActualityAggregatedStatsBarChart: React.FC<
  ActualityAggregatedStatsBarChartProps
> = props => {
  const { t } = useTranslation('feed');
  const getNamedColor = useGetNamedColor();
  const aggFieldsDescriptors = useActualityAggregationFieldDescriptors();

  const groupBy = useRecoilValue(actualityAggregatedStatsGroupBy);
  const sortBy = useRecoilValue(
    actualityAggregatedStatsSelectedAggregatedMetric,
  );

  // Get the field key for the x-axis (the index field)
  const xAxisField = props.xAxisField || groupBy; // Use prop or fallback to groupBy
  const xAxisFieldDescriptor = aggFieldsDescriptors[xAxisField];

  // Use shared date formatting function with field context
  const formatAxisLabel = (value: any) =>
    formatChartAxisLabel(value, xAxisField, xAxisFieldDescriptor);

  const chartKeys = new Set<string>();
  const chartData = _(props.data)
    .groupBy(item => item.x)
    .map((items, key) => {
      return _.transform(
        items,
        (acc, item) => {
          const dimensionKey = item[groupBy]?.toString() ?? 'UNKNOWN';
          chartKeys.add(dimensionKey);
          acc[dimensionKey] = item.y;
        },
        { index: key },
      );
    })
    .value();

  return (
    <ContainerChart

    // containerHeight={
    //   props.compareMode
    //     ? '40%'
    //     : props.compareMode && props.fullHeight
    //       ? '50%'
    //       : !props.compareMode && props.fullHeight
    //         ? '90%'
    //         : '40%'
    // }
    >
      <ResponsiveBarCanvas
        data={chartData}
        indexBy={'index'}
        keys={[...chartKeys]}
        indexScale={{ type: 'band', round: false }}
        colors={datum => getNamedColor(datum.id as string)[0]}
        valueFormat={v => formatInteger(v)}
        margin={{
          ...commonMarginConfig,
          bottom: 10,
        }}
        padding={0}
        innerPadding={0}
        axisTop={null}
        axisRight={null}
        axisLeft={null}
        enableGridY={false}
        axisBottom={
          props.showAxisX
            ? {
                tickSize: 0,
                tickPadding: 20,
                tickRotation: 0,
                format: formatAxisLabel,
              }
            : null
        }
        borderWidth={2}
        borderColor={colors.app.background}
        labelSkipHeight={12}
        labelTextColor={datum => getBestContrastColor(datum.color)}
        tooltip={datum => (
          <ActualityAggregatedStatsBarChartTooltip datum={datum} />
        )}
        theme={{
          axis: {
            ticks: {
              text: {
                fontSize: 13,
                fill: theme.ticks,
              },
            },
            legend: {
              text: {
                fontSize: 15,
                fill: theme.legend,
              },
            },
            // domain: {
            //   line: {
            //     stroke: theme.grid,
            //     strokeWidth: 0,
            //   },
            // },
          },
        }}
      />
    </ContainerChart>
  );
};

export default ActualityAggregatedStatsBarChart;
